/**
 * LMS API Client
 *
 * Frontend API client for the Learning Management System.
 * Handles GP course proxying, local course CRUD, enrollments,
 * progress tracking, quizzes, and awards.
 */

import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

function headers(token: string) {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

// ==================== GP Course Proxy ====================

export const getGPCourses = async (
  token: string,
  options?: { search?: string; category?: string; limit?: number; offset?: number }
) => {
  try {
    const params = new URLSearchParams();
    if (options?.search) params.append("search", options.search);
    if (options?.category) params.append("category", options.category);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    const qs = params.toString();
    const response = await axios.get(`${BASE_URL}/lms/courses/gp${qs ? `?${qs}` : ""}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const getGPCourse = async (token: string, courseId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/lms/courses/gp/${courseId}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const getGPLessonContent = async (token: string, courseId: string, lessonId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/lms/courses/gp/${courseId}/lessons/${lessonId}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const getGPQuiz = async (token: string, courseId: string, quizId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/lms/courses/gp/${courseId}/quizzes/${quizId}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Local Courses ====================

export const getLocalCourses = async (
  token: string,
  options?: { search?: string; category?: string; context?: string; published?: string }
) => {
  try {
    const params = new URLSearchParams();
    if (options?.search) params.append("search", options.search);
    if (options?.category) params.append("category", options.category);
    if (options?.context) params.append("context", options.context);
    if (options?.published) params.append("published", options.published);
    const qs = params.toString();
    const response = await axios.get(`${BASE_URL}/lms/courses/local${qs ? `?${qs}` : ""}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const createLocalCourse = async (token: string, data: Record<string, any>) => {
  try {
    const response = await axios.post(`${BASE_URL}/lms/courses/local`, data, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const updateLocalCourse = async (token: string, courseId: string, data: Record<string, any>) => {
  try {
    const response = await axios.put(`${BASE_URL}/lms/courses/local/${courseId}`, data, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const deleteLocalCourse = async (token: string, courseId: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/lms/courses/local/${courseId}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Combined ====================

export const getAllCourses = async (
  token: string,
  options?: { search?: string; category?: string; context?: string }
) => {
  try {
    const params = new URLSearchParams();
    if (options?.search) params.append("search", options.search);
    if (options?.category) params.append("category", options.category);
    if (options?.context) params.append("context", options.context);
    const qs = params.toString();
    const response = await axios.get(`${BASE_URL}/lms/courses${qs ? `?${qs}` : ""}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Local Course Structure ====================

export const getCourseStructure = async (token: string, courseId: string) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/lms/courses/local/${courseId}/structure`,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ---- Sections ----

export const createSection = async (
  token: string,
  courseId: string,
  data: { title: string; description?: string; order?: number }
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/courses/local/${courseId}/sections`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const updateSection = async (
  token: string,
  courseId: string,
  sectionId: string,
  data: Record<string, any>
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/lms/courses/local/${courseId}/sections/${sectionId}`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const deleteSection = async (token: string, courseId: string, sectionId: string) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/lms/courses/local/${courseId}/sections/${sectionId}`,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const reorderSections = async (token: string, courseId: string, ids: string[]) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/courses/local/${courseId}/sections/reorder`,
      { ids },
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const reorderSectionItems = async (
  token: string,
  courseId: string,
  section_id: string,
  items: Array<{ id: string; type: "lesson" | "quiz" }>
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/courses/local/${courseId}/section-items/reorder`,
      { section_id, items },
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ---- Lessons ----

export interface LessonContentBag {
  body?: string;
  video_url?: string;
  document_url?: string;
  embed_html?: string;
  attachments?: Array<{ name: string; url: string }>;
}

export const createLesson = async (
  token: string,
  courseId: string,
  data: {
    section_id: string;
    title: string;
    description?: string;
    lesson_type?: "text" | "video" | "document" | "embed";
    content?: LessonContentBag;
    duration_minutes?: number;
    is_preview?: boolean;
  }
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/courses/local/${courseId}/lessons`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const updateLesson = async (
  token: string,
  courseId: string,
  lessonId: string,
  data: Record<string, any>
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/lms/courses/local/${courseId}/lessons/${lessonId}`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const deleteLesson = async (token: string, courseId: string, lessonId: string) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/lms/courses/local/${courseId}/lessons/${lessonId}`,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ---- Quizzes + Questions ----

export const createQuiz = async (
  token: string,
  courseId: string,
  data: {
    section_id: string;
    title: string;
    description?: string;
    passing_score?: number;
    time_limit_minutes?: number;
  }
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const updateQuiz = async (
  token: string,
  courseId: string,
  quizId: string,
  data: Record<string, any>
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes/${quizId}`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const deleteQuiz = async (token: string, courseId: string, quizId: string) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes/${quizId}`,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const listQuizQuestions = async (token: string, courseId: string, quizId: string) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes/${quizId}/questions`,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const createQuizQuestion = async (
  token: string,
  courseId: string,
  quizId: string,
  data: {
    question: string;
    question_type?: "multiple_choice" | "true_false" | "short_answer";
    options?: string[];
    correct_answer: string;
    points?: number;
  }
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes/${quizId}/questions`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const updateQuizQuestion = async (
  token: string,
  courseId: string,
  quizId: string,
  questionId: string,
  data: Record<string, any>
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes/${quizId}/questions/${questionId}`,
      data,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const deleteQuizQuestion = async (
  token: string,
  courseId: string,
  quizId: string,
  questionId: string
) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/lms/courses/local/${courseId}/quizzes/${quizId}/questions/${questionId}`,
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Enrollments ====================

export const getEnrollments = async (
  token: string,
  options?: { athlete_id?: string; course_id?: string; status?: string; course_source?: string }
) => {
  try {
    const params = new URLSearchParams();
    if (options?.athlete_id) params.append("athlete_id", options.athlete_id);
    if (options?.course_id) params.append("course_id", options.course_id);
    if (options?.status) params.append("status", options.status);
    if (options?.course_source) params.append("course_source", options.course_source);
    const qs = params.toString();
    const response = await axios.get(`${BASE_URL}/lms/enrollments${qs ? `?${qs}` : ""}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const createEnrollment = async (
  token: string,
  data: { athlete_id: string; course_source: string; course_id?: string; gp_course_id?: string }
) => {
  try {
    const response = await axios.post(`${BASE_URL}/lms/enrollments`, data, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const updateEnrollment = async (token: string, id: string, data: Record<string, any>) => {
  try {
    const response = await axios.put(`${BASE_URL}/lms/enrollments/${id}`, data, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const dropEnrollment = async (token: string, id: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/lms/enrollments/${id}`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Progress ====================

export const getEnrollmentProgress = async (token: string, enrollmentId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/lms/enrollments/${enrollmentId}/progress`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// The server computes progress from the course's own lessons (PM d2199358),
// so no lesson count is sent.
export const markLessonComplete = async (
  token: string,
  enrollmentId: string,
  lessonId: string
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/enrollments/${enrollmentId}/lessons/${lessonId}/complete`,
      {},
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const submitQuiz = async (
  token: string,
  enrollmentId: string,
  quizId: string,
  answers: Array<{ question_id: string; answer: string }>,
  gpCourseId?: string
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lms/enrollments/${enrollmentId}/quizzes/${quizId}/submit`,
      { answers, gp_course_id: gpCourseId },
      { headers: headers(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Awards ====================

export const getAthleteAwards = async (token: string, athleteId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/lms/athletes/${athleteId}/awards`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const issueAward = async (token: string, enrollmentId: string, data: Record<string, any>) => {
  try {
    const response = await axios.post(`${BASE_URL}/lms/enrollments/${enrollmentId}/award`, data, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// ==================== Overview ====================

export const getLMSOverview = async (token: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/lms/overview`, { headers: headers(token) });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};
